// The book on a timeline — the cutting room.
//
// The shape is a video editor's: a browser on the left (the scenes of the
// book, and its media library), the picture in the middle, the inspector of
// whatever is selected on the right, and the time underneath, the width of
// the room. What draws the preview is the player's own engine and stage
// (components/Player), so the author watches the reader's player rather than
// an impression of it.
//
// Every operation is the one the app editor performs, in the same words:
// scenes are pages (book_player/player_state.lua), a picture has two
// keyframes and a move between them, words have a render box and a style, a
// scene carries music, an effect and tap areas.
import React, { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react"
import { BackwardIcon, ForwardIcon, PauseIcon, PlayIcon } from "@heroicons/react/24/solid"
import {
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  ArrowsPointingOutIcon,
  Cog6ToothIcon,
  CursorArrowRaysIcon,
  DocumentDuplicateIcon,
  MusicalNoteIcon,
  PhotoIcon,
  PlayIcon as PlayOutlineIcon,
  PlusIcon,
  Squares2X2Icon,
  StopIcon,
  TrashIcon,
} from "@heroicons/react/24/outline"
import { twMerge } from "tailwind-merge"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import useProductEditorTexts from "aldoo-ra/CMSModels/StoreProduct/useProductEditorTexts"
import PlayerEngine from "../../../Player/engine"
import Stage from "../../../Player/Stage"
import { CONTENT_LAYOUTS, formatTime, getPageImage, imageAttrs, layoutOrder, setContentLayout } from "../../../Player/layout"
import { LoadProductMedia, MediaOperation, UploadLocalizedAudio, UploadLocalizedImage } from "../../../AdminShared/api"
import { errorMessage } from "../../../AdminShared/ui"
import AudioRecorder from "../shared/AudioRecorder"
import { fileName } from "../shared/MediaPicker"
import { urljoin } from "../shared/useProductEditor"
import StageTools from "./StageTools"
import Inspector from "./Inspector"
import SettingsDialog from "./SettingsDialog"
import { TRANSITIONS, bookOps, idOf, layerLayout } from "./editorModel"
import { makeResolver, resolveProduct } from "./resolvePage"
import "../../../Player/player.css"

/**
 * Holding the pointer through a drag. A pointer that is already gone (a
 * synthetic event, a cancelled touch) throws rather than answering, and a
 * drag is not worth an exception.
 */
const capture = (event) => {
  try {
    event.currentTarget.setPointerCapture?.(event.pointerId)
  } catch {}
}
const release = (event) => {
  try {
    event.currentTarget.releasePointerCapture?.(event.pointerId)
  } catch {}
}

/** The width of the lane names down the left of the timeline. */
const LABELS = 88

const LANES = [
  { key: "scenes", label: "Scenes", tall: true, testid: "timeline-scene", selects: "scene" },
  { key: "motion", label: "Picture", testid: "timeline-motion", selects: "background" },
  { key: "text", label: "Text", testid: "timeline-text", selects: "text" },
  { key: "voice", label: "Voice", testid: "timeline-voice", selects: "text" },
  { key: "music", label: "Music", testid: "timeline-music", selects: "audio" },
  { key: "sfx", label: "Sound FX", testid: "timeline-sfx", selects: "audio" },
  { key: "triggers", label: "Tap areas", testid: "timeline-trigger", selects: "trigger" },
]

/** A second of the book, in pixels. The zoom levels a cut is made at. */
const ZOOMS = [4, 8, 16, 32, 64]

/**
 * The tools, as a video editor keeps them: one is in hand at a time and it
 * decides what a press on the picture does.
 */
const TOOLS = [
  { key: "select", label: "Select", hint: "Pick things on the picture, drag the picture itself (V)", icon: CursorArrowRaysIcon, shortcut: "v" },
  { key: "text", label: "Text", hint: "Place and size the words (T)", icon: Squares2X2Icon, shortcut: "t" },
  { key: "image", label: "Image", hint: "Put a picture on this scene (I)", icon: PhotoIcon, shortcut: "i" },
  { key: "audio", label: "Audio", hint: "Music, effect and voice-over (A)", icon: MusicalNoteIcon, shortcut: "a" },
  { key: "trigger", label: "Tap area", hint: "Draw an area that answers a tap (R)", icon: ArrowsPointingOutIcon, shortcut: "r" },
  { key: "delete", label: "Delete", hint: "Remove what is selected (Backspace)", icon: TrashIcon, shortcut: "x" },
]

/** What the footer calls the part of the scene in hand. */
const KIND_LABELS = { scene: "Scene", background: "Picture", text: "Text", audio: "Sound", trigger: "Tap area" }

/** The layer of a page the previewed layout reads (phone, then its fallbacks). */
const layer = (obj) => {
  for (const name of layoutOrder()) if (obj?.[name]) return obj[name]
  return null
}

const notify = (message, isError = false) =>
  showNotification({ message, className: isError ? "bg-admin_error text-white" : "bg-admin_success text-white" })

export default function SceneEditor({ editor, selected = 0, onSelect, onClose, extras }) {
  const t = useProductEditorTexts()
  const { pid, text, setText, language, setLanguage, defaultLanguage, mediaRoot, durations } = editor
  const [media, setMedia] = useState(editor.media || [])
  useEffect(() => setMedia(editor.media || []), [editor.media])

  // the preview reads what a reader would: the keys of the document resolved
  // to text and urls (resolvePage.js)
  const resolver = useMemo(
    () => makeResolver({ text, language, defaultLanguage, mediaRoot, durations }),
    [text, language, defaultLanguage, mediaRoot, durations]
  )
  const product = useMemo(() => resolveProduct(editor.product, resolver), [editor.product, resolver])
  const pages = product?.pages || []

  // The book is written in its own languages; the admin's chosen language may
  // not be one of them, and editing then writes values no reader will see.
  // The app editor picks from `contents.languages` for exactly this reason
  // (book_player/player_state.lua `currentLanguage`), so a language the book
  // does not have falls back to the book's first.
  const languages = editor.product?.languages || []
  const editLanguage = languages.includes(language) ? language : languages[0] || language
  const editing = useMemo(() => ({ ...editor, language: editLanguage }), [editor, editLanguage])
  const ops = useMemo(() => bookOps(editing, { notify }), [editing])

  const engineRef = useRef(null)
  if (!engineRef.current) engineRef.current = new PlayerEngine()
  const engine = engineRef.current
  const state = useSyncExternalStore(engine.subscribe, engine.getSnapshot, engine.getSnapshot)
  const audioRef = useRef(null)

  const [layout, setLayout] = useState("phone")
  const [zoom, setZoom] = useState(16)
  const [tool, setTool] = useState("select")
  // what the inspector is describing: a part of the current scene
  const [selection, setSelection] = useState({ kind: "scene" })
  const [showTriggers, setShowTriggers] = useState(true)
  const [overlays, setOverlays] = useState(true)
  // which of the two keyframes a drag on the picture is setting
  const [phase, setPhase] = useState("end")
  const [browser, setBrowser] = useState("scenes")
  const [showInspector, setShowInspector] = useState(true)
  const [settings, setSettings] = useState(false)
  const [recorder, setRecorder] = useState(false)
  // the library plays a sound where it stands, as the Lua library does on a
  // second click (Screens/media_library.lua `playAudioPreview`)
  const [playing, setPlaying] = useState(null)
  const preview = useRef(null)
  const [busy, setBusy] = useState("")
  const [search, setSearch] = useState("")
  const [mediaType, setMediaType] = useState("image")
  const [showUsed, setShowUsed] = useState(true)
  const stageRef = useRef(null)
  const timelineRef = useRef(null)
  const scrubbing = useRef(false)
  const fileInput = useRef(null)
  const uploadTarget = useRef({ id: null, type: "image" })
  const [stageBox, setStageBox] = useState({ width: 0, height: 0 })

  useEffect(() => {
    const element = stageRef.current
    if (!element || typeof ResizeObserver === "undefined") return
    const measure = () => setStageBox({ width: element.clientWidth, height: element.clientHeight })
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    engine.attachAudio(audioRef.current)
    return () => engine.destroy()
  }, [engine])

  // The pages are being edited while this runs: reload when what the engine
  // reads changes — a scene added or removed, a picture, a voice or a length
  // swapped. Moving a keyframe does not, or every drag would rebuild the book
  // underneath the hand doing the dragging.
  const signature = useMemo(
    () =>
      JSON.stringify(
        pages.map((item) => {
          const subtitle = layer(item.subtitle)
          const background = layer(item.background)
          return [getPageImage(item), subtitle?.audio, subtitle?.audioDuration, subtitle?.startAt, background?.duration, item.music, item.sfx]
        })
      ),
    [pages]
  )
  useEffect(() => {
    if (!pages.length) return
    // `load` counts pages from one (it is what a reader's url says); staying
    // on the scene being worked on is what an editor expects
    const stay = Math.min(engine.state.pageIndex, pages.length - 1)
    engine.load(product, { autoPlay: false, page: stay + 1 })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, layout])

  /**
   * Which keyframe is being edited. The picture is drawn by running its move
   * from the start transform to the end one, so showing a keyframe means
   * putting the scene's clock at that end of the move — `lm.Time = 0` or
   * `lm.Duration` in features/editor.lua.
   */
  const choosePhase = useCallback(
    (name) => {
      setPhase(name)
      const scene = pages[engine.state.pageIndex]
      const move = Number(layer(scene?.background)?.duration) || 0
      engine.pause()
      engine.seekInPage(name === "start" ? 0 : move)
    },
    [engine, pages]
  )

  const chooseLayout = (name) => {
    setContentLayout(name)
    setLayout(name)
  }

  const { timing, total } = engine
  const pageIndex = Math.min(state.pageIndex, Math.max(0, pages.length - 1))
  const page = pages[pageIndex]
  const raw = editor.product?.pages?.[pageIndex]
  const position = engine.globalPosition()

  /**
   * While the book is not playing, the picture stands at the keyframe being
   * edited — otherwise a drag changes numbers the preview is not showing.
   * Moving to another scene keeps the same keyframe in view.
   */
  useEffect(() => {
    if (!pages.length || !state.paused) return
    const move = Number(layer(pages[pageIndex]?.background)?.duration) || 0
    engine.seekInPage(phase === "start" ? 0 : move)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, phase, state.paused])

  const pick = useCallback(
    (index, next) => {
      if (index === null || index === undefined) return
      engine.goToPage(index, { at: 0, play: false })
      onSelect?.(index)
      if (next) setSelection(next)
    },
    [engine, onSelect]
  )

  /** Select a part of the current scene (and, given one, another scene). */
  const select = (next, index) => {
    if (index !== null && index !== undefined) pick(index)
    if (next) setSelection(next)
  }

  // ---- media library ----------------------------------------------------

  const refreshLibrary = async () => {
    const res = await LoadProductMedia(pid)
    if (res?.media) setMedia(res.media)
  }

  /** The key of the current selection a picture or a sound is written to. */
  const targetKey = (type) => {
    if (!raw) return null
    if (type === "image") return idOf(raw.background?.[layerLayout(raw.background, layout)]?.image)
    if (selection.kind === "audio") return idOf(raw.sfx) || idOf(raw.music)
    if (selection.kind === "trigger") return idOf(raw.triggerAreas?.[selection.order ?? 0]?.audio)
    return idOf(raw.subtitle?.[layerLayout(raw.subtitle, layout)]?.audio)
  }

  const applyMedia = (item) => {
    const id = targetKey(item.type)
    if (!id) return notify(t`Nothing to put that on — select a scene part first`, true)
    setText(id, item.url, editLanguage)
    notify(item.type === "image" ? t("Picture set on scene %s", pageIndex + 1) : t("Sound set on scene %s", pageIndex + 1))
  }

  const askUpload = (type, id) => {
    uploadTarget.current = { id: id || targetKey(type), type }
    if (!uploadTarget.current.id) return notify(t`Select what the file is for first`, true)
    fileInput.current.accept = type === "image" ? "image/*" : "audio/*"
    fileInput.current.click()
  }

  /** Put a file on a key, the way an upload does. */
  const uploadTo = async (file, id, type) => {
    if (!file || !id) return
    setBusy("upload")
    try {
      const res = type === "image" ? await UploadLocalizedImage(file, { id, language: editLanguage }) : await UploadLocalizedAudio(file, { id, language: editLanguage })
      if (errorMessage(res) || !res?.url) return notify(errorMessage(res) || t`Upload failed`, true)
      setText(id, res.url, editLanguage)
      await refreshLibrary()
      notify(type === "image" ? t("Image uploaded (%s)", editLanguage.toUpperCase()) : t("Audio uploaded (%s)", editLanguage.toUpperCase()))
    } finally {
      setBusy("")
    }
  }

  const onFileChosen = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = ""
    const { id, type } = uploadTarget.current
    await uploadTo(file, id, type)
  }

  const stopPreview = useCallback(() => {
    preview.current?.pause()
    preview.current = null
    setPlaying(null)
  }, [])

  const togglePreview = (item) => {
    if (playing === item.url) return stopPreview()
    stopPreview()
    const sound = new Audio(urljoin(mediaRoot, item.url))
    sound.onended = () => setPlaying(null)
    sound.onerror = () => {
      notify(t`That sound could not be played`, true)
      setPlaying(null)
    }
    preview.current = sound
    setPlaying(item.url)
    sound.play().catch(() => setPlaying(null))
  }

  useEffect(() => stopPreview, [stopPreview])

  const deleteMedia = (item) =>
    showPromptDialog({
      title: t`Delete media file`,
      message: t("Delete %s? Scenes using it will show nothing.", fileName(item.url)),
      confirmText: t`Delete`,
      onConfirm: async () => {
        const res = await MediaOperation({ pid, type: item.type, operation: "delete", mediaId: item.url.split("/").pop() })
        const failed = res ? errorMessage(res) : ""
        notify(failed || t`Deleted`, !!failed)
        await refreshLibrary()
      },
    })

  /** Which files the book is already using — the library's used / unused filter. */
  const usedUrls = useMemo(() => {
    const used = new Set()
    for (const item of editor.product?.pages || []) {
      for (const name of CONTENT_LAYOUTS) {
        const image = text(idOf(item.background?.[name]?.image), editLanguage)
        if (image) used.add(image)
        const audio = text(idOf(item.subtitle?.[name]?.audio), editLanguage)
        if (audio) used.add(audio)
      }
      for (const key of [item.music, item.sfx]) {
        const value = text(idOf(key), editLanguage)
        if (value) used.add(value)
      }
      for (const trigger of item.triggerAreas || []) {
        const value = text(idOf(trigger.audio), editLanguage)
        if (value) used.add(value)
      }
    }
    return used
  }, [editor.product, text, editLanguage])

  const libraryItems = useMemo(
    () =>
      media
        .filter((item) => item.type === mediaType)
        .filter((item) => (showUsed ? true : !usedUrls.has(item.url)))
        .filter((item) => !search || fileName(item.url).toLowerCase().includes(search.toLowerCase())),
    [media, mediaType, showUsed, usedUrls, search]
  )

  // ---- scenes -----------------------------------------------------------

  const addScene = async () => {
    setBusy("scene")
    try {
      const index = await ops.addPage(pageIndex + 1)
      if (index !== null) pick(index, { kind: "background" })
    } finally {
      setBusy("")
    }
  }

  const cloneScene = async (index = pageIndex) => {
    setBusy("scene")
    try {
      const at = await ops.clonePage(index)
      if (at !== null) pick(at)
    } finally {
      setBusy("")
    }
  }

  const deleteScene = (index = pageIndex) =>
    showPromptDialog({
      title: t("Delete scene %s", index + 1),
      message: t`Remove this scene from the book? Its texts and media stay in the library.`,
      confirmText: t`Delete`,
      onConfirm: () => pick(ops.deletePage(index)),
    })

  /** Dragging a scene in the browser puts it somewhere else in the book. */
  const dragScene = useRef(null)
  const dropScene = (to) => {
    const from = dragScene.current
    dragScene.current = null
    if (from === null || from === undefined || from === to) return
    pick(ops.movePage(from, to))
  }

  /**
   * A picture dropped on a scene becomes its background for the layout being
   * previewed. The document holds a content key there, so what is written is
   * the key's value.
   */
  const dropImage = (index, url) => {
    const target = editor.product?.pages?.[index]
    const id = idOf(target?.background?.[layerLayout(target?.background, layout)]?.image)
    if (!id || !url) return
    setText(id, url, editLanguage)
  }

  /** The delete tool, and the Backspace key: remove whatever is in hand. */
  const deleteSelected = () => {
    if (selection.kind === "trigger" && raw?.triggerAreas?.length) {
      ops.deleteTrigger(pageIndex, selection.order ?? 0)
      return setSelection({ kind: "trigger", order: Math.max(0, (selection.order ?? 0) - 1) })
    }
    if (selection.kind === "background") return setText(targetKey("image"), "", editLanguage)
    if (selection.kind === "text") return setText(idOf(layer(raw?.subtitle)?.text), "", editLanguage)
    if (selection.kind === "audio") {
      const key = idOf(raw?.sfx) || idOf(raw?.music)
      return setText(key, "", editLanguage)
    }
    deleteScene()
  }

  const addTrigger = async () => {
    const order = await ops.addTrigger(pageIndex)
    if (order !== null) setSelection({ kind: "trigger", order })
  }

  // ---- keys -------------------------------------------------------------

  useEffect(() => {
    const body = document.body
    const was = body.style.overflow
    body.style.overflow = "hidden"
    const typing = (target) => /^(INPUT|TEXTAREA|SELECT)$/.test(target?.tagName) || target?.isContentEditable
    const onKey = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation()
        return onClose?.()
      }
      const key = event.key.toLowerCase()
      // undo and redo work while typing too, the way they do in a text field
      if ((event.metaKey || event.ctrlKey) && key === "z") {
        event.preventDefault()
        return event.shiftKey ? editor.redo?.() : editor.undo?.()
      }
      if ((event.metaKey || event.ctrlKey) && key === "y") {
        event.preventDefault()
        return editor.redo?.()
      }
      if (typing(event.target)) return
      const shortcut = TOOLS.find((item) => item.shortcut === key)
      if (shortcut) return setTool(shortcut.key)
      if (event.key === " ") {
        event.preventDefault()
        return engine.toggle()
      }
      if (event.key === "Backspace" || event.key === "Delete") {
        event.preventDefault()
        return deleteSelected()
      }
      if (event.key === "[") return engine.prev()
      if (event.key === "]") return engine.next()
    }
    window.addEventListener("keydown", onKey)
    return () => {
      body.style.overflow = was
      window.removeEventListener("keydown", onKey)
    }
  }, [onClose, engine, selection, pageIndex, raw, editor])

  // the tool in hand decides what the inspector is describing
  useEffect(() => {
    if (tool === "text") setSelection({ kind: "text" })
    if (tool === "image") setSelection({ kind: "background" })
    if (tool === "audio") setSelection({ kind: "audio" })
    if (tool === "trigger") setSelection((current) => (current.kind === "trigger" ? current : { kind: "trigger", order: 0 }))
  }, [tool])

  // ---- the timeline -----------------------------------------------------

  const seekAt = (clientX) => {
    const track = timelineRef.current
    if (!track) return
    const box = track.getBoundingClientRect()
    const x = clientX - box.left + track.scrollLeft - LABELS
    engine.seekTo(Math.max(0, Math.min(x / zoom, total)))
  }

  const startScrub = (event) => {
    if (event.button !== 0) return
    scrubbing.current = true
    capture(event)
    seekAt(event.clientX)
  }
  const moveScrub = (event) => {
    if (!scrubbing.current) return
    event.preventDefault()
    seekAt(event.clientX)
  }
  const endScrub = (event) => {
    scrubbing.current = false
    release(event)
  }

  /**
   * Dragging a clip: the body moves what it is (when a text appears, where a
   * scene sits in the book), the edge changes how long it lasts. Both are
   * measured in seconds, which at this zoom is what a pixel is worth.
   */
  const clip = useRef(null)
  const clipMoved = useRef(false)
  const [dropAt, setDropAt] = useState(null)
  const startClip = (event, item, mode) => {
    if (event.button !== 0) return
    capture(event)
    clip.current = { item, mode, x: event.clientX, applied: 0 }
    clipMoved.current = false
  }
  const moveClip = (event) => {
    const current = clip.current
    if (!current) return
    event.preventDefault()
    event.stopPropagation()
    const seconds = (event.clientX - current.x) / zoom
    const delta = seconds - current.applied
    if (Math.abs(delta) < 0.05) return
    clipMoved.current = true
    current.applied = seconds
    if (current.mode === "resize") return current.item.onResize?.(delta)
    if (current.item.onMove) return current.item.onMove(delta)
    // a scene being carried somewhere else shows where it would land; the
    // book is only rearranged when it is dropped
    if (current.item.onDragTo) setDropAt(current.item.targetAt?.(seconds) ?? null)
  }
  const endClip = (event) => {
    const current = clip.current
    clip.current = null
    setDropAt(null)
    release(event)
    if (current?.item?.onDragTo && Math.abs(current.applied) > 0.3) {
      const moved = current.item.onDragTo(current.applied)
      if (moved !== undefined && moved !== null) pick(moved)
    }
    // a click that followed a drag should not also re-select
    setTimeout(() => {
      clipMoved.current = false
    }, 0)
  }

  // the playhead stays in sight while the book plays
  useEffect(() => {
    const track = timelineRef.current
    if (!track || scrubbing.current) return
    const x = LABELS + position * zoom
    const left = track.scrollLeft
    const right = left + track.clientWidth
    if (x < left + LABELS + 40) track.scrollLeft = Math.max(0, x - LABELS - 40)
    else if (x > right - 80) track.scrollLeft = x - track.clientWidth + 160
  }, [position, zoom])

  /** The seconds the ruler names, spaced so the marks never crowd. */
  const ticks = useMemo(() => {
    const step = zoom >= 64 ? 1 : zoom >= 32 ? 2 : zoom >= 16 ? 5 : zoom >= 8 ? 15 : 30
    const out = []
    for (let second = 0; second <= total; second += step) out.push(second)
    return out
  }, [total, zoom])

  /**
   * What is on the timeline, lane by lane: each event knows the second it
   * starts at and how long it lasts, so it is drawn where it happens rather
   * than after whatever was drawn before it.
   */
  const { events, keyframes } = useMemo(() => {
    const lanes = { scenes: [], motion: [], text: [], voice: [], music: [], sfx: [], triggers: [] }
    const marks = []

    pages.forEach((item, index) => {
      const at = timing[index]?.start || 0
      const duration = timing[index]?.duration || 0
      const source = editor.product?.pages?.[index] || {}
      const subtitle = layer(item.subtitle)
      const background = layer(item.background)
      const subtitleLayout = layerLayout(source.subtitle, layout)
      const backgroundLayout = layerLayout(source.background, layout)
      const rawSubtitle = source.subtitle?.[subtitleLayout] || {}
      const rawBackground = source.background?.[backgroundLayout] || {}
      const write = (path, value) => editor.set(`pages.${index}.${path}`, value)
      const round1 = (value) => Math.round(value * 10) / 10

      lanes.scenes.push({
        key: `scene-${index}`,
        page: index,
        start: at,
        duration,
        label: `${index + 1}. ${item.title || ""}`,
        title: `${item.title || t("Scene %s", index + 1)} · ${formatTime(duration)}`,
        thumb: imageAttrs(getPageImage(item)),
        className: "bg-admin_neutral_1 border-admin_bordered hover:bg-admin_neutral_2 text-admin_text",
        // a scene dragged along the timeline changes its place in the book
        targetAt: (seconds) => {
          const at = (timing[index]?.start || 0) + seconds
          let target = 0
          for (let i = 0; i < timing.length; i++) if (at >= timing[i].start) target = i
          return target
        },
        onDragTo: (seconds) => {
          const at = (timing[index]?.start || 0) + seconds
          let target = 0
          for (let i = 0; i < timing.length; i++) if (at >= timing[i].start) target = i
          return ops.movePage(index, target)
        },
      })

      const anim = Number(background?.duration) || 0
      if (anim > 0) {
        lanes.motion.push({
          key: `motion-${index}`,
          page: index,
          start: at,
          duration: anim,
          label: `${t(source.transition || TRANSITIONS[0])} · ${anim}s`,
          title: t("Picture moves for %ss, in on %s · drag the edge to change the length", anim, t(source.transition || TRANSITIONS[0])),
          thumb: imageAttrs(getPageImage(item)),
          className: "bg-admin_info/25 border-admin_info/50 text-admin_text",
          onResize: (seconds) => write(`background.${backgroundLayout}.duration`, Math.max(0, round1((rawBackground.duration || 0) + seconds))),
        })
      }
      marks.push({ key: `kf-${index}-start`, page: index, phase: "start", at, title: t("Scene %s · start keyframe", index + 1) })
      marks.push({ key: `kf-${index}-end`, page: index, phase: "end", at: at + duration, title: t("Scene %s · end keyframe", index + 1) })

      if (subtitle?.text) {
        const from = Number(subtitle.startAt) || 0
        const length = Number(subtitle.duration) || Math.max(0, duration - from)
        lanes.text.push({
          key: `text-${index}`,
          page: index,
          start: at + from,
          duration: length,
          label: String(subtitle.text).slice(0, 80),
          title: `${subtitle.text}\n\n${t`Drag to move when it appears, drag the edge for how long it stays`}`,
          className: "bg-admin_warning/25 border-admin_warning/50 text-admin_text",
          onMove: (seconds) => write(`subtitle.${subtitleLayout}.startAt`, Math.max(0, round1((rawSubtitle.startAt || 0) + seconds))),
          onResize: (seconds) => write(`subtitle.${subtitleLayout}.duration`, Math.max(0, round1((Number(rawSubtitle.duration) || length) + seconds))),
        })
      }

      if (subtitle?.audio) {
        const spoken = Number(subtitle.audioDuration) || 0
        lanes.voice.push({
          key: `voice-${index}`,
          page: index,
          start: at + (Number(subtitle.startAt) || 0),
          duration: spoken,
          label: formatTime(spoken),
          title: t("Voice-over · %s · drag to move when it starts", formatTime(spoken)),
          className: "bg-admin_success/30 border-admin_success/60 text-admin_text",
          // the voice and the words share the moment they begin at
          onMove: (seconds) => write(`subtitle.${subtitleLayout}.startAt`, Math.max(0, round1((rawSubtitle.startAt || 0) + seconds))),
        })
      }

      if (item.music) {
        lanes.music.push({
          key: `music-${index}`,
          page: index,
          start: at,
          duration: source.musicLoop ? duration : Math.min(Number(item.musicDuration) || duration, duration),
          label: item.musicDuration ? t("music %s", formatTime(item.musicDuration)) : t`music`,
          title: source.musicLoop ? t`Music, looping until the scene ends` : t`Music under this scene`,
          className: "bg-admin_primary/25 border-admin_primary/50 text-admin_text",
        })
      }

      if (item.sfx) {
        const shot = Number(item.sfxDuration) || 0
        lanes.sfx.push({
          key: `sfx-${index}`,
          page: index,
          start: at,
          duration: source.sfxLoop || !shot ? duration : Math.min(shot, duration),
          label: source.sfxLoop ? t("sfx ⟳ %s", shot ? formatTime(shot) : "") : t("sfx %s", shot ? formatTime(shot) : ""),
          title: source.sfxLoop ? t`Sound effect, looping until the scene ends` : t`Sound effect, once`,
          className: "bg-admin_warning/30 border-admin_warning/60 text-admin_text",
        })
      }

      ;(item.triggerAreas || []).forEach((trigger, order) => {
        lanes.triggers.push({
          key: `trigger-${index}-${order}`,
          page: index,
          order,
          start: at,
          duration,
          label: trigger.gotoPage ? t("tap %s → %s", order + 1, trigger.gotoPage) : trigger.audio ? t("tap %s ♪", order + 1) : t("tap %s", order + 1),
          title: trigger.gotoPage ? t("Tap area, jumps to scene %s", trigger.gotoPage) : t`Tap area`,
          className: "bg-admin_error/20 border-admin_error/40 text-admin_text",
        })
      })
    })

    return { events: lanes, keyframes: marks }
  }, [pages, timing, editor, layout, ops, state.pageIndex, t])

  return (
    <div className="fixed inset-0 z-[400] flex flex-col bg-admin_canvas text-admin_text" data-testid="scene-editor">
      <audio ref={audioRef} preload="auto" crossOrigin="anonymous" className="hidden" />
      <input ref={fileInput} type="file" className="hidden" onChange={onFileChosen} data-testid="editor-file" />

      {/* the room's title bar */}
      <header className="shrink-0 h-[46px] px-3 flex items-center gap-2 border-b border-admin_bordered bg-admin_paper">
        <span className="text-sm font-semibold truncate max-w-[220px]">{editor.product?.name || t`Content`}</span>
        <span className="text-xs text-admin_text_secondary whitespace-nowrap">{t("%s scenes", pages.length)}</span>

        <span className="w-px h-6 bg-admin_bordered mx-1" />

        {/* the tools: one in hand at a time */}
        <div className="inline-flex gap-0.5" role="toolbar" aria-label={t`Tools`}>
          {TOOLS.map((item) => {
            const Icon = item.icon
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => (item.key === "delete" ? deleteSelected() : setTool(item.key))}
                title={t(item.hint)}
                className={twMerge(
                  "w-8 h-8 rounded-md inline-flex items-center justify-center transition-colors",
                  tool === item.key ? "bg-admin_primary text-white" : "text-admin_text_secondary hover:bg-admin_neutral_1 hover:text-admin_text"
                )}
                data-testid={`tool-${item.key}`}
                aria-pressed={tool === item.key}
              >
                <Icon className="w-4 h-4" />
              </button>
            )
          })}
        </div>

        <span className="w-px h-6 bg-admin_bordered mx-1" />

        <button
          type="button"
          onClick={() => editor.undo?.()}
          disabled={!editor.canUndo}
          title={t`Undo (⌘Z)`}
          className="w-8 h-8 rounded-md inline-flex items-center justify-center hover:bg-admin_neutral_1 disabled:opacity-40"
          data-testid="undo"
        >
          <ArrowUturnLeftIcon className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.redo?.()}
          disabled={!editor.canRedo}
          title={t`Redo (⇧⌘Z)`}
          className="w-8 h-8 rounded-md inline-flex items-center justify-center hover:bg-admin_neutral_1 disabled:opacity-40"
          data-testid="redo"
        >
          <ArrowUturnRightIcon className="w-4 h-4" />
        </button>

        <span className="w-px h-6 bg-admin_bordered mx-1" />

        <button type="button" onClick={addScene} disabled={!!busy} title={t`Add a scene after this one`} className="h-8 px-2 rounded-md text-xs inline-flex items-center gap-1 hover:bg-admin_neutral_1" data-testid="add-scene">
          <PlusIcon className="w-4 h-4" /> {t`Scene`}
        </button>
        <button type="button" onClick={() => cloneScene()} disabled={!!busy} title={t`Duplicate this scene`} className="h-8 px-2 rounded-md text-xs inline-flex items-center gap-1 hover:bg-admin_neutral_1" data-testid="clone-scene">
          <DocumentDuplicateIcon className="w-4 h-4" />
        </button>

        <span className="w-px h-6 bg-admin_bordered mx-1" />

        <div className="inline-flex p-1 rounded-lg bg-admin_neutral_1 gap-1">
          {CONTENT_LAYOUTS.map((name) => (
            <button
              key={name}
              type="button"
              onClick={() => chooseLayout(name)}
              className={twMerge(
                "px-2.5 h-6 rounded-md text-xs capitalize transition-colors",
                layout === name ? "bg-admin_paper text-admin_text font-semibold shadow-sm" : "text-admin_text_secondary hover:text-admin_text"
              )}
              data-testid={`layout-${name}`}
            >
              {t(name)}
            </button>
          ))}
        </div>

        {/* which language the words and the media belong to */}
        <select
          value={editLanguage}
          onChange={(event) => setLanguage(event.target.value)}
          disabled={languages.length < 2}
          className="h-7 px-2 rounded-md bg-admin_neutral_1 text-xs disabled:opacity-70"
          aria-label={t`Language`}
          data-testid="editor-language"
          title={languages.length < 2 ? t`The only language this book is written in` : t`The language being edited`}
        >
          {(languages.length ? languages : [editLanguage]).map((code) => (
            <option key={code} value={code}>
              {String(code).toUpperCase()}
            </option>
          ))}
        </select>

        <label className="flex items-center gap-1.5 text-xs text-admin_text_secondary whitespace-nowrap">
          <input type="checkbox" checked={overlays} onChange={(event) => setOverlays(event.target.checked)} data-testid="toggle-tools" />
          {t`Handles`}
        </label>

        <span className="flex-1" />

        <button type="button" onClick={() => setSettings(true)} title={t`Book settings`} className="w-8 h-8 rounded-md inline-flex items-center justify-center hover:bg-admin_neutral_1" data-testid="open-settings">
          <Cog6ToothIcon className="w-5 h-5" />
        </button>
        <label className="flex items-center gap-1.5 text-xs text-admin_text_secondary">
          <input type="checkbox" checked={showInspector} onChange={(event) => setShowInspector(event.target.checked)} data-testid="toggle-inspector" />
          {t`Inspector`}
        </label>
        <button type="button" onClick={onClose} className="h-7 px-3 rounded-md text-xs bg-admin_neutral_1 hover:bg-admin_neutral_2" data-testid="scene-editor-close">
          {t`Close`}
        </button>
      </header>

      {/* browser on the left, viewer in the middle, inspector on the right */}
      <div className="flex-1 min-h-0 flex">
        <aside className="w-[290px] 2xl:w-[290px] xl:w-[240px] lg:w-[200px] shrink-0 border-r border-admin_bordered bg-admin_paper flex flex-col">
          <div className="flex shrink-0 border-b border-admin_bordered">
            {["scenes", "media"].map((name) => (
              <button
                key={name}
                type="button"
                onClick={() => setBrowser(name)}
                className={twMerge(
                  "flex-1 h-8 text-xs capitalize",
                  browser === name ? "bg-admin_neutral_1 text-admin_text font-semibold" : "text-admin_text_secondary hover:text-admin_text"
                )}
                data-testid={`browser-${name}`}
              >
                {t(name)}
              </button>
            ))}
          </div>

          {browser === "scenes" && (
            <div className="flex-1 overflow-y-auto p-2 flex flex-col gap-1" data-testid="scene-list">
              {pages.map((item, index) => {
                const attrs = imageAttrs(getPageImage(item))
                return (
                  <div
                    key={index}
                    draggable
                    onDragStart={() => {
                      dragScene.current = index
                    }}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={(event) => {
                      event.preventDefault()
                      const url = event.dataTransfer.getData("text/plain")
                      if (url) return dropImage(index, url)
                      dropScene(index)
                    }}
                    onClick={() => pick(index)}
                    className={twMerge(
                      "group flex gap-2 items-center p-1 rounded-md border text-left transition-colors cursor-pointer",
                      index === pageIndex ? "border-admin_primary bg-admin_primary/10" : "border-transparent hover:bg-admin_neutral_1"
                    )}
                    data-testid="scene-item"
                  >
                    <span className="w-[62px] h-[40px] shrink-0 rounded bg-admin_neutral_1 overflow-hidden">
                      {attrs && <img src={attrs.src} srcSet={attrs.srcSet} alt="" className="w-full h-full object-cover" />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-xs truncate">{item.title || t("Scene %s", index + 1)}</span>
                      <span className="block text-[11px] text-admin_text_secondary tabular-nums">
                        {index + 1} · {formatTime(timing[index]?.duration || 0)}
                      </span>
                    </span>
                    <span className="flex flex-col opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        type="button"
                        title={t`Duplicate`}
                        onClick={(event) => {
                          event.stopPropagation()
                          cloneScene(index)
                        }}
                        className="w-6 h-5 inline-flex items-center justify-center text-admin_text_secondary hover:text-admin_text"
                      >
                        <DocumentDuplicateIcon className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        title={t`Delete`}
                        onClick={(event) => {
                          event.stopPropagation()
                          deleteScene(index)
                        }}
                        className="w-6 h-5 inline-flex items-center justify-center text-admin_text_secondary hover:text-admin_error"
                      >
                        <TrashIcon className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  </div>
                )
              })}
              <button
                type="button"
                onClick={addScene}
                disabled={!!busy}
                className="mt-1 h-9 rounded-md border border-dashed border-admin_bordered text-xs text-admin_text_secondary hover:text-admin_text hover:border-admin_primary inline-flex items-center justify-center gap-1"
                data-testid="add-scene-list"
              >
                <PlusIcon className="w-4 h-4" /> {t`Add a scene`}
              </button>
            </div>
          )}

          {browser === "media" && (
            <div className="flex-1 min-h-0 flex flex-col" data-testid="media-strip">
              <div className="p-2 flex flex-col gap-2 border-b border-admin_bordered">
                <div className="flex gap-1">
                  {["image", "audio"].map((name) => (
                    <button
                      key={name}
                      type="button"
                      onClick={() => setMediaType(name)}
                      className={twMerge(
                        "flex-1 h-7 rounded-md text-xs capitalize",
                        mediaType === name ? "bg-admin_primary text-white" : "bg-admin_neutral_1 text-admin_text_secondary hover:text-admin_text"
                      )}
                      data-testid={`media-type-${name}`}
                    >
                      {name === "image" ? t`images` : t`sounds`}
                    </button>
                  ))}
                </div>
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder={t`Search the library`}
                  className="h-7 px-2 rounded-md bg-admin_neutral_1 text-xs outline-none"
                  data-testid="media-search"
                />
                <label className="flex items-center gap-1.5 text-[11px] text-admin_text_secondary">
                  <input type="checkbox" checked={showUsed} onChange={(event) => setShowUsed(event.target.checked)} />
                  {t`Show files the book already uses`}
                </label>
                <button
                  type="button"
                  onClick={() => askUpload(mediaType)}
                  disabled={busy === "upload"}
                  className="h-7 rounded-md border border-dashed border-admin_bordered text-xs text-admin_text_secondary hover:text-admin_text hover:border-admin_primary"
                  data-testid="media-upload"
                >
                  {busy === "upload" ? t`Uploading…` : mediaType === "image" ? t`Upload a picture` : t`Upload a sound`}
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-2">
                <p className="m-0 mb-2 text-[11px] text-admin_text_secondary">
                  {t("%s of %s · click to put it on the selection, drag a picture onto a scene", libraryItems.length, media.filter((item) => item.type === mediaType).length)}
                </p>
                {mediaType === "image" ? (
                  <div className="grid grid-cols-3 gap-1">
                    {libraryItems.map((item) => (
                      <div key={item.url} className="relative group">
                        <img
                          src={urljoin(mediaRoot, item.url)}
                          alt=""
                          title={fileName(item.url)}
                          draggable
                          onDragStart={(event) => event.dataTransfer.setData("text/plain", item.url)}
                          onClick={() => applyMedia(item)}
                          className={twMerge(
                            "h-[52px] w-full object-cover rounded border cursor-grab",
                            usedUrls.has(item.url) ? "border-admin_primary/60" : "border-admin_bordered"
                          )}
                          data-testid="media-item"
                        />
                        <button
                          type="button"
                          onClick={() => deleteMedia(item)}
                          title={t`Delete from the library`}
                          className="absolute top-0.5 right-0.5 w-5 h-5 rounded bg-black/60 text-white opacity-0 group-hover:opacity-100 inline-flex items-center justify-center"
                        >
                          <TrashIcon className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col gap-0.5">
                    {libraryItems.map((item) => (
                      <div key={item.url} className="group flex items-center gap-1 text-[11px] py-0.5" data-testid="media-item">
                        <button
                          type="button"
                          onClick={() => togglePreview(item)}
                          title={playing === item.url ? t`Stop` : t`Listen`}
                          className={twMerge(
                            "w-5 h-5 shrink-0 rounded inline-flex items-center justify-center",
                            playing === item.url ? "bg-admin_primary text-white" : "text-admin_text_secondary hover:text-admin_text hover:bg-admin_neutral_1"
                          )}
                          data-testid="media-preview"
                        >
                          {playing === item.url ? <StopIcon className="w-3 h-3" /> : <PlayOutlineIcon className="w-3 h-3" />}
                        </button>
                        <button type="button" onClick={() => applyMedia(item)} className="min-w-0 flex-1 text-left truncate hover:text-admin_primary" title={fileName(item.url)}>
                          {usedUrls.has(item.url) ? "● " : "○ "}
                          {fileName(item.url)}
                        </button>
                        <span className="tabular-nums text-admin_text_secondary">{durations?.[item.url] ? `${Math.round(durations[item.url])}s` : ""}</span>
                        <button
                          type="button"
                          onClick={() => deleteMedia(item)}
                          title={t`Delete from the library`}
                          className="opacity-0 group-hover:opacity-100 text-admin_text_secondary hover:text-admin_error"
                        >
                          <TrashIcon className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {!libraryItems.length && <span className="text-xs text-admin_text_secondary italic">{t`Nothing here.`}</span>}
                {extras && <div className="mt-4 pt-3 border-t border-admin_bordered">{extras}</div>}
              </div>
            </div>
          )}
        </aside>

        {/* the viewer */}
        {/* the picture keeps the room: the panels give way before it does */}
        <section className="flex-1 min-w-[320px] flex flex-col bg-black/90">
          <div className="flex-1 min-h-0 flex items-center justify-center p-4">
            <div ref={stageRef} className="relative h-full max-h-full aspect-video max-w-full shrink">
              <Stage
                product={product}
                page={page}
                engine={engine}
                state={state}
                triggers={showTriggers && !overlays}
                className="w-full h-full rounded-md"
              />
              {overlays && (
                <StageTools
                  editor={editing}
                  page={raw}
                  resolved={page}
                  pageIndex={pageIndex}
                  viewport={stageBox}
                  phase={phase}
                  onPhase={choosePhase}
                  tool={tool}
                  selection={selection}
                  onSelect={setSelection}
                  onAddTrigger={addTrigger}
                />
              )}
            </div>
          </div>

          <div className="shrink-0 h-[46px] px-3 flex items-center gap-2 bg-admin_paper border-t border-admin_bordered">
            <button type="button" onClick={() => engine.prev()} className="w-8 h-8 rounded-full hover:bg-admin_neutral_1 inline-flex items-center justify-center" aria-label={t`Previous scene`}>
              <BackwardIcon className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => engine.toggle()}
              className="w-9 h-9 rounded-full bg-admin_primary text-white inline-flex items-center justify-center"
              aria-label={state.paused ? t`Play` : t`Pause`}
              data-testid="scene-play"
            >
              {state.paused ? <PlayIcon className="w-5 h-5 translate-x-[1px]" /> : <PauseIcon className="w-5 h-5" />}
            </button>
            <button type="button" onClick={() => engine.next()} className="w-8 h-8 rounded-full hover:bg-admin_neutral_1 inline-flex items-center justify-center" aria-label={t`Next scene`}>
              <ForwardIcon className="w-4 h-4" />
            </button>
            <span className="text-xs tabular-nums text-admin_text_secondary" data-testid="scene-time">
              {formatTime(position)} / {formatTime(total)}
            </span>
            <span className="text-xs text-admin_text_secondary ml-3">
              {t("Scene %s · %s", pageIndex + 1, t(KIND_LABELS[selection.kind] || selection.kind))}
              {selection.kind === "background" ? ` · ${t("%s keyframe", t(phase))}` : ""}
            </span>
            <span className="flex-1" />
            <span className="text-xs text-admin_text_secondary">{t`Zoom`}</span>
            <input
              type="range"
              min={0}
              max={ZOOMS.length - 1}
              value={ZOOMS.indexOf(zoom)}
              onChange={(event) => setZoom(ZOOMS[Number(event.target.value)])}
              className="w-[120px]"
              aria-label={t`Timeline zoom`}
            />
          </div>
        </section>

        {/* the inspector: what the selected thing is made of */}
        {showInspector && (
          <aside className="w-[400px] xl:w-[340px] lg:w-[300px] shrink-0 border-l border-admin_bordered bg-admin_paper overflow-y-auto p-3">
            <Inspector
              editor={editing}
              ops={ops}
              selection={selection}
              onSelect={select}
              layout={layout}
              pageIndex={pageIndex}
              durations={durations}
              onPickMedia={(type) => {
                setBrowser("media")
                setMediaType(type)
              }}
              onUploadMedia={askUpload}
              onRecord={() => setRecorder(true)}
              phase={phase}
              onPhase={choosePhase}
            />
          </aside>
        )}
      </div>

      {/* the timeline, the width of the room */}
      <div className="shrink-0 h-[232px] border-t border-admin_bordered bg-admin_paper overflow-auto relative" data-testid="timeline" ref={timelineRef}>
        <div className="relative" style={{ width: LABELS + Math.max(total * zoom, 320) }}>
          <div
            className="sticky top-0 z-20 h-6 bg-admin_paper border-b border-admin_bordered select-none cursor-ew-resize"
            onPointerDown={startScrub}
            onPointerMove={moveScrub}
            onPointerUp={endScrub}
            onPointerCancel={endScrub}
            data-testid="timeline-ruler"
          >
            {ticks.map((second) => (
              <span
                key={second}
                className="absolute top-0 h-full pl-1 text-[10px] text-admin_text_secondary tabular-nums border-l border-admin_bordered/60 pointer-events-none"
                style={{ left: LABELS + second * zoom }}
              >
                {formatTime(second)}
              </span>
            ))}
          </div>

          <div className="relative cursor-ew-resize" onPointerDown={startScrub} onPointerMove={moveScrub} onPointerUp={endScrub} onPointerCancel={endScrub}>
            {LANES.map((lane) => (
              <div key={lane.key} className={twMerge("relative border-b border-admin_bordered/40", lane.tall ? "h-[46px]" : "h-[30px]")}>
                <span className="sticky left-0 z-10 float-left h-full w-[88px] px-2 flex items-center text-[11px] text-admin_text_secondary bg-admin_paper border-r border-admin_bordered/60">
                  {t(lane.label)}
                </span>
                {events[lane.key].map((event) => (
                  <button
                    key={event.key}
                    type="button"
                    title={event.title}
                    onPointerDown={(e) => {
                      e.stopPropagation()
                      if (!event.onMove && !event.onDragTo) return
                      startClip(e, event, "move")
                    }}
                    onPointerMove={(e) => moveClip(e)}
                    onPointerUp={(e) => endClip(e)}
                    onPointerCancel={(e) => endClip(e)}
                    onClick={(e) => {
                      e.stopPropagation()
                      if (clipMoved.current) return
                      pick(event.page, lane.selects === "trigger" ? { kind: "trigger", order: event.order ?? 0 } : { kind: lane.selects })
                    }}
                    onDragOver={lane.key === "scenes" ? (e) => e.preventDefault() : undefined}
                    onDrop={
                      lane.key === "scenes"
                        ? (e) => {
                            e.preventDefault()
                            const url = e.dataTransfer.getData("text/plain")
                            if (url) dropImage(event.page, url)
                          }
                        : undefined
                    }
                    className={twMerge(
                      "absolute top-[3px] bottom-[3px] rounded-[3px] border text-[10px] leading-tight text-left px-1 overflow-hidden whitespace-nowrap",
                      event.className,
                      event.page === pageIndex && "ring-1 ring-admin_primary"
                    )}
                    style={{ left: LABELS + event.start * zoom, width: Math.max(event.duration * zoom, 3) }}
                    data-testid={lane.testid}
                  >
                    {event.thumb && (
                      <img
                        src={event.thumb.src}
                        srcSet={event.thumb.srcSet}
                        alt=""
                        loading="lazy"
                        className="float-left h-full w-[36px] object-cover mr-1 -ml-1 rounded-l-[3px] pointer-events-none"
                      />
                    )}
                    {event.label}
                    {event.onResize && (
                      <span
                        role="presentation"
                        title={t`Drag to change how long it lasts`}
                        onPointerDown={(e) => {
                          e.stopPropagation()
                          startClip(e, event, "resize")
                        }}
                        onPointerMove={(e) => moveClip(e)}
                        onPointerUp={(e) => endClip(e)}
                        className="absolute inset-y-0 right-0 w-1.5 cursor-ew-resize bg-admin_text/30 hover:bg-admin_primary"
                        data-testid="clip-handle"
                      />
                    )}
                  </button>
                ))}

                {lane.key === "motion" &&
                  keyframes.map((mark) => (
                    <button
                      key={mark.key}
                      type="button"
                      title={mark.title}
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation()
                        pick(mark.page, { kind: "background" })
                        choosePhase(mark.phase)
                      }}
                      className={twMerge(
                        "absolute top-1/2 w-2.5 h-2.5 -ml-[5px] -mt-[5px] rotate-45 border z-10",
                        mark.page === pageIndex && phase === mark.phase ? "bg-admin_primary border-admin_primary" : "bg-admin_paper border-admin_text_secondary"
                      )}
                      style={{ left: LABELS + mark.at * zoom }}
                      data-testid={`keyframe-${mark.phase}`}
                    />
                  ))}
              </div>
            ))}
          </div>

          {dropAt !== null && timing[dropAt] && (
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-admin_primary z-20 pointer-events-none"
              style={{ left: LABELS + timing[dropAt].start * zoom }}
              data-testid="drop-marker"
            />
          )}

          <div className="absolute top-0 bottom-0 w-px bg-admin_error z-30 pointer-events-none" style={{ left: LABELS + position * zoom }} data-testid="playhead">
            <span
              className="absolute -top-0 -ml-[6px] w-3 h-3 rounded-b-sm bg-admin_error pointer-events-auto cursor-ew-resize"
              onPointerDown={startScrub}
              onPointerMove={moveScrub}
              onPointerUp={endScrub}
              onPointerCancel={endScrub}
              data-testid="playhead-handle"
            />
          </div>
        </div>
      </div>

      {settings && <SettingsDialog editor={editor} onClose={() => setSettings(false)} />}

      {/* reading the scene aloud, straight onto its voice-over key — the
          recorder the Lua text panel opens (OpenAudioRecorder) */}
      <AudioRecorder
        open={recorder}
        title={t("%s — scene %s / %s", editor.product?.name || pid, pageIndex + 1, pages.length)}
        textContent={layer(page?.subtitle)?.text || ""}
        busy={busy === "upload"}
        onUseRecording={(file) => uploadTo(file, idOf(layer(raw?.subtitle)?.audio), "audio")}
        onNextPage={() => engine.next()}
        onPreviousPage={() => engine.prev()}
        onClose={() => setRecorder(false)}
      />
    </div>
  )
}
